'use client';

import Button from '@/components/Button';
import Image from '@/components/Image';
import Price from '@/components/Product/Price';
import { Product } from '@/components/Product/ProductCard';
import ProductTag from '@/components/Product/ProductTag';
import Wish from '@/components/Product/Wish';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { toast } from '@/components/Toast';
import { useStore } from '@/providers/app-store-provider';
import { colors, font } from '@/theme/zeelool.theme';
import { DIRECTLY_ADD_TO_CART_PARAMS } from '@/utils/constants';
import { genPrice } from '@/utils/price';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// Import Swiper styles
import { addToCart } from '@/api/cart';
import { fetchMeetConditionsApi } from '@/api/public';
import ImagePreview, { ImagePreviewRef } from '@/components/ImagePreview';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useInView } from 'react-intersection-observer';
import Member from '@/components/Product/Member';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';
export interface RelatedRecommendListProps {
  recommendList: Product[];
  uuid?: string;
  isWeb?: boolean;
  showSkeleton?: boolean;
  sdProductDetailsPageComplementMktExp?: (params: any, index: number) => void;
  sdProductDetailsPageComplementMktValidExp?: (params: any, index: number) => void;
  sdProductDetailsComplementMktClick?: (params: any, index: number) => void;
}

interface ItemComponentProps {
  product: Product;
  uuid?: string;
  isWeb?: boolean;
  sdProductDetailsPageComplementMktExp?: (params: any, index: number) => void;
  sdProductDetailsPageComplementMktValidExp?: (params: any, index: number) => void;
  sdProductDetailsComplementMktClick?: (params: any, index: number) => void;
  index?: number;
}

const ItemComponent: React.FC<ItemComponentProps> = ({
  product,
  uuid,
  isWeb = false,
  sdProductDetailsPageComplementMktExp,
  sdProductDetailsPageComplementMktValidExp,
  sdProductDetailsComplementMktClick,
  index,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [item] = useState(product);
  const currency = useStore((state) => state.currency);
  const setCart = useStore((state) => state.setCart);
  const router = useRouter();
  const imagePreviewRef = useRef<ImagePreviewRef>(null);
  const t = useTranslations('components.goodsDetail.relatedRecommendList');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);

  const [itemRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      sdProductDetailsPageComplementMktExp?.(item, index);
    }
  }, [inView]);

  const goodsDetailLinkRef = useRef<HTMLDivElement>(null);

  // 飞入购物车动画函数
  const flyToCartAnimation = (element: HTMLElement) => {
    // 获取原始图片的位置和尺寸
    const rect = element.getBoundingClientRect();

    // 创建克隆元素
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.position = 'fixed';
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    clone.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    clone.style.borderRadius = '8px';

    // 添加到页面中
    document.body.appendChild(clone);
    // 设置目标位置（页面右上角）
    const rightDistance = isWeb ? 100 : 50; // 距离右边100px
    const targetX = window.innerWidth - rightDistance;
    const targetY = 50; // 距离顶部50px
    // 执行动画
    requestAnimationFrame(() => {
      clone.style.left = `${targetX}px`;
      clone.style.top = `${targetY}px`;
      clone.style.width = '20px';
      clone.style.height = '20px';
      clone.style.opacity = '0.3';
    });

    // 动画结束后移除元素
    setTimeout(() => {
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
    }, 800);
  };

  // 加购功能
  const onAddToCartClick = async () => {
    SDAddToCartBtnClick(item);
    const option = { ...DIRECTLY_ADD_TO_CART_PARAMS };
    if (item.is_glass === 1) {
      option.prescription_type = 'Frame Only';
      option.lens_id = 1;
    }
    const addToCartParams = {
      currency: currency.name,
      product_id: item.id,
      qty: 1,
      option,
    };

    try {
      setBtnLoading(true);
      const { code } = await addToCart(addToCartParams);
      if (code === 200) {
        toast.success('successfully added');
        setCart();

        // 触发飞入购物车动画
        const productImgElement = goodsDetailLinkRef.current?.querySelector(
          '.good-img',
        ) as HTMLElement;
        if (productImgElement) {
          // 创建飞入购物车动画
          flyToCartAnimation(productImgElement);
        }
        if (!isWeb) {
          const res = await fetchMeetConditionsApi({ condition_2: 3 });
          if (res.code === 200) {
            if (res.data.is_meet_conditions) {
              router.push(`/activity-page?id=${res.data.active_page_id}`);
            }
          }
        }
      }

      SDAddToCartResult(item, `${code}`, code === 200);
    } catch (error: any) {
      logger.error(error);
      SDAddToCartResult(item, `${error.code}`, false);
    } finally {
      setBtnLoading(false);
    }
  };

  // 埋点事件
  const SDAddToCartBtnClick = (params: Product) => {
    try {
      const priceObj = genPrice(params.price_info, currency.name);
      const basePriceObj = genPrice(params.price_info, env.NEXT_PUBLIC_DEFAULT_CURRENCY);
      const sdEventParams = {
        product_view_uuid: uuid,
        entrance_edit_lens_scene: '',
        edit_lens_type: '',
        add_cart_scene: '商详页配饰推荐位直接加购',
        product_class_name: (params as any).products_category || '',
        is_product_lens: params.is_glass === 1 && (params as any).product_lens === 1,
        shop_sku: params.shop_sku,
        business_sku: (params as any).business_sku || params.shop_sku,
        product_sku: (params as any).sku || params.shop_sku,
        product_id: `${params.id}`,
        product_name: params.short_name,
        is_glass: params.is_glass === 1,
        prescription_type_name: params.is_glass === 1 ? 'Frame Only' : '',
        product_price: parseFloat(priceObj.price),
        current_product_price: parseFloat(priceObj.sell_price),
        base_current_product_price: parseFloat(basePriceObj.sell_price),
        currency: currency.name,
      };

      if (typeof window !== 'undefined' && (window as any).$sensors) {
        (window as any).$sensors.sensorsTrack('AddToCartBtn_Click', sdEventParams);
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const SDAddToCartResult = (params: Product, service_code: string, isSuccess: boolean) => {
    try {
      const priceObj = genPrice(params.price_info, currency.name);
      const basePriceObj = genPrice(params.price_info, env.NEXT_PUBLIC_DEFAULT_CURRENCY);
      const sdEventParams = {
        product_view_uuid: uuid,
        entrance_edit_lens_scene: '',
        edit_lens_type: '',
        add_cart_scene: '商详页配饰推荐位直接加购',
        product_class_name: (params as any).products_category || '',
        is_product_lens: params.is_glass === 1 && (params as any).product_lens === 1,
        shop_sku: params.shop_sku,
        business_sku: (params as any).business_sku || params.shop_sku,
        product_sku: (params as any).sku || params.shop_sku,
        product_id: `${params.id}`,
        product_name: params.short_name,
        is_glass: params.is_glass === 1,
        prescription_type_name: params.is_glass === 1 ? 'Frame Only' : '',
        product_price: parseFloat(priceObj.price),
        current_product_price: parseFloat(priceObj.sell_price),
        base_current_product_price: parseFloat(basePriceObj.sell_price),
        currency: currency.name,
        dollar_exchange_rate: parseFloat(currency.exchange_rate),
        is_success: isSuccess,
        service_code,
      };

      if (typeof window !== 'undefined' && (window as any).$sensors) {
        (window as any).$sensors.sensorsTrack('AddToCart_Result', sdEventParams);
      }
    } catch (e) {
      logger.error(e);
    }
  };

  // 创建售罄覆盖层组件
  const SoldOutBlock = () => {
    // ! 周边推荐的功能，数据不返回售罄和暂无库存的数据，下面的逻辑也看不到，暂时不翻译
    if (item.status === 2) {
      return (
        <div className="absolute inset-0 z-[21] flex items-center justify-center">
          <div className="flex flex-col items-center justify-center aspect-square h-[30%] bg-black bg-opacity-60 rounded-full text-xs text-white">
            <span className="mb-1">No</span>
            <span>Restock</span>
          </div>
        </div>
      );
    }
    if (item.stock === 0) {
      return (
        <div className="absolute inset-0 z-[21] flex items-center justify-center">
          <div className="flex flex-col items-center justify-center aspect-square h-[30%] bg-black bg-opacity-60 rounded-full text-xs text-white">
            <span className="mb-1">Sold</span>
            <span>Out</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const allImages = useMemo(() => {
    try {
      const { image: { product: productImages = [] } = {} } = item;
      return productImages;
    } catch (error) {
      logger.error(error);
      return [];
    }
  }, [item.image]);

  const productImage = useMemo(() => {
    const { image: { product: productImages = [] } = {} } = item;
    return productImages[0] || '';
  }, [item.image]);

  const handleImageClick = useCallback((_index: number) => {
    imagePreviewRef.current?.handleVisibleChange(_index);
  }, []);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => sdProductDetailsPageComplementMktValidExp?.(item, index),
  });

  const mergedRef = useMergedRefs(itemRef, validExposureRef);

  if (isWeb) {
    // PC端布局
    return (
      <div
        ref={mergedRef}
        className="acc-item"
        onClick={() => sdProductDetailsComplementMktClick?.(item, index)}
      >
        <div
          ref={goodsDetailLinkRef}
          className="rr-might-like-item flex flex-col items-center justify-center text-[#333333] w-full"
        >
          {/* 商品图片容器 */}
          <div className="img-wrap relative w-full h-[17.34vw]">
            <Image
              className={`good-img w-full h-full object-cover rounded-xl overflow-hidden cursor-pointer ${
                item.stock === 0 ? 'opacity-50' : ''
              }`}
              src={productImage}
              alt={item.short_name || ''}
              width={333}
              height={333}
              onClick={() => handleImageClick(0)}
            />
            <ImagePreview
              ref={imagePreviewRef}
              images={allImages}
              showThumbnail={false}
              sx={{ display: 'none' }}
            />
            <div className="wish absolute top-[0.83vw] right-[0.83vw]">
              <Wish
                data={item as any}
                className="text-14 leading-[24px] text-auxiliary-dark-gray"
                wishNumClassName="ml-2 text-14"
                iconClassName="text-14"
                showWishNum={false}
                loginSituation="配饰推荐收藏"
                collectScene="商详页配饰推荐"
              />
            </div>

            {/* 售罄覆盖层 */}
            <SoldOutBlock />
          </div>

          {/* 商品信息 */}
          <div className="bottom-box flex justify-between items-center w-full px-[0.83vw] mt-8 box-border">
            <div className="left-box flex justify-start items-center whitespace-nowrap flex-wrap">
              <span className="item-name text-14 mr-[0.52vw]">{item.short_name}</span>
              {token && userInfo?.id ? (
                <Member
                  level={userInfo.member_level}
                  productData={item}
                  className="mr-6 text-[11px]"
                />
              ) : null}
              <Price
                priceObject={item as any}
                renderPrice={(price, sellPrice) => (
                  <div className="price text-14">
                    <span
                      className={`n-price notranslate font-bb ${price !== sellPrice ? 'sale' : ''}`}
                      aria-label={tPriceA11y('srCurrent') + sellPrice}
                    >
                      {sellPrice}
                    </span>
                    {price !== sellPrice && (
                      <span className="o-price notranslate ml-2 text-[#a9a9a9] line-through text-12">
                        <del aria-label={tPriceA11y('srOriginal') + price}>{price}</del>
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            {/* 加购按钮 */}
            <Button
              className="add-btn"
              onClick={onAddToCartClick}
              loading={btnLoading}
              disabled={item.stock <= 0 || item.status === 2}
              variant="outlined"
              sx={{
                padding: '8px 12px',
                height: '1.67vw',
                border: '1px solid #808080',
                color: '#ffffff',
                fontSize: '12px',
                fontFamily: font.br,
                borderRadius: '1.04vw',
                backgroundColor: colors.primaryBlack,
                flex: 'none',
                '&:hover': {
                  backgroundColor: colors.primaryBlackHover,
                },
                '& .MuiButton-loadingIndicator': {
                  color: '#ffffff',
                },
              }}
            >
              {t('addToCart')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 移动端布局（保持原有样式）
  return (
    <div
      ref={mergedRef}
      className="item text-center text-sm relative bg-white overflow-hidden"
      onClick={() => sdProductDetailsComplementMktClick?.(item, index)}
    >
      <div ref={goodsDetailLinkRef}>
        {/* 商品图片容器 */}
        <div className="img-block w-[160px] h-[160px] relative overflow-hidden rounded-xl bg-gray-100">
          {/* 商品图片 */}
          <Image
            className="good-img w-full h-full object-cover"
            src={productImage}
            alt={item.short_name || ''}
            width={160}
            height={160}
            onClick={() => handleImageClick(0)}
          />
          <ImagePreview
            ref={imagePreviewRef}
            images={allImages}
            showThumbnail={false}
            sx={{ display: 'none' }}
          />

          {/* 使用 ProductCardMedia 的标签和收藏部分 */}
          <div
            className={`absolute top-0 left-0 w-full flex items-center justify-between ${isWeb ? 'p-[16px]' : 'p-[6px]'}`}
          >
            {/* 左侧标签区域 - 即使不展示标签也要保持占位 */}
            <div className="flex h-[24px] items-center">
              <ProductTag product={item} />
            </div>
            {/* 右侧收藏按钮区域 TODO item 跟data类型不匹配 @刘行 */}
            <Wish
              data={item as any}
              className={`leading-[24px] text-auxiliary-dark-gray ${isWeb ? 'text-14' : 'text-12'}`}
              wishNumClassName="ml-2 text-14"
              iconClassName={`text-14`}
              showWishNum={false}
              loginSituation="配饰推荐收藏"
              collectScene="商详页配饰推荐"
            />
          </div>

          {/* 售罄覆盖层 */}
          <SoldOutBlock />
        </div>

        {/* 商品信息 */}
        <div className="info-wrap px-2 pt-2.5 text-left relative z-10">
          <div className="name flex items-center justify-between">
            <span className="sku-name inline-block text-xs leading-3.5 text-[#666666]">
              {item.short_name}
            </span>
          </div>

          <Price
            priceObject={item as any}
            renderPrice={(price, sellPrice) => (
              <div className="flex items-center text-14 whitespace-nowrap mt-6">
                {token && userInfo?.id ? (
                  <Member
                    level={userInfo.member_level}
                    productData={item}
                    className="mr-6 text-[11px]"
                  />
                ) : null}
                <span aria-label={tPriceA11y('srCurrent') + sellPrice} className="font-bb">
                  {sellPrice}
                </span>
                {price !== sellPrice && (
                  <span className="line-through text-[#a9a9a9] text-12 ml-8">
                    <del aria-label={tPriceA11y('srOriginal') + price}>{price}</del>
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* 加购按钮 */}
        <div className="btn_wrap mt-16 flex items-center justify-center">
          <Button
            variant="outlined"
            disabled={item.stock <= 0 || item.status === 2}
            onClick={onAddToCartClick}
            sx={{
              width: '144px',
              height: '28px',
              borderRadius: '14px',
              border: '1px solid #808080',
              color: colors.primaryBlack,
              fontSize: '12px',
              fontFamily: font.bb,
              padding: 0,
            }}
            loading={btnLoading}
          >
            {t('addToCart')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// 骨架屏组件 - PC端
const SkeletonItemPC = () => (
  <div className="acc-item">
    <div className="rr-might-like-item flex flex-col items-center justify-center text-[#333333] w-full">
      {/* 商品图片骨架 */}
      <div className="img-wrap relative w-full h-[17.34vw] bg-gray-200 rounded-xl animate-pulse" />

      {/* 商品信息骨架 */}
      <div className="bottom-box flex justify-between items-center w-full px-[0.83vw] mt-8 box-border gap-4">
        <div className="h-[1.67vw] w-[120px] bg-gray-200 rounded-[1.04vw] animate-pulse" />
        <div className="h-[1.67vw] w-[120px] bg-gray-200 rounded-[1.04vw] animate-pulse" />
      </div>
    </div>
  </div>
);

// 骨架屏组件 - 移动端
const SkeletonItemMobile = () => (
  <div className="list_item">
    <div className="rr-might-like-item flex flex-col">
      {/* 商品图片骨架 */}
      <div className="img-wrap relative w-[160px] h-[160px] bg-gray-200 rounded-lg animate-pulse" />

      {/* 商品信息骨架 */}
      <div className="bottom-box mt-8 px-8">
        <div className="h-[14px] w-[100px] bg-gray-200 rounded animate-pulse mb-6" />
        <div className="h-[14px] w-[60px] bg-gray-200 rounded animate-pulse mb-8" />
        <div className="h-[32px] w-full bg-gray-200 rounded-lg animate-pulse" />
      </div>
    </div>
  </div>
);

const RelatedRecommendList: React.FC<RelatedRecommendListProps> = ({
  recommendList,
  uuid,
  sdProductDetailsPageComplementMktExp,
  sdProductDetailsPageComplementMktValidExp,
  sdProductDetailsComplementMktClick,
  isWeb = false,
  showSkeleton = false,
}) => {
  const t = useTranslations('components.goodsDetail.relatedRecommendList');
  // 判断是否显示骨架屏
  const shouldShowSkeleton = showSkeleton && (!recommendList || recommendList.length === 0);
  // 骨架屏显示数量
  const skeletonList: unknown[] = Array(6).fill(null);

  if (isWeb) {
    // PC端布局
    return (
      <div className="acc-list-wrap font-regular relative bg-white w-full box-border px-[45px] py-[60px]">
        <h3 className="text-center font-normal font-bb text-[#333333] text-[24px]">{t('title')}</h3>

        <div className="swiper-wrap mt-[2.08vw] mx-auto py-[9px] box-border swiper-nav-container relative">
          {shouldShowSkeleton ? (
            // PC端骨架屏 - 显示4列网格
            <div className="grid grid-cols-4 gap-[20px]">
              {skeletonList.slice(0, 4).map((_, index) => (
                <SkeletonItemPC key={index} />
              ))}
            </div>
          ) : (
            <>
              <Swiper
                className="rr-might-like-swiper"
                slidesPerView={4}
                slidesPerGroup={4}
                spaceBetween={20}
                navigation={{
                  nextEl: '.related-recommend-next',
                  prevEl: '.related-recommend-prev',
                }}
                modules={[Navigation]}
                observer
                observeParents
                observeSlideChildren
              >
                {recommendList.map((item, index) => (
                  <SwiperSlide key={index}>
                    <ItemComponent
                      product={item}
                      uuid={uuid}
                      isWeb={isWeb}
                      index={index}
                      sdProductDetailsPageComplementMktExp={sdProductDetailsPageComplementMktExp}
                      sdProductDetailsPageComplementMktValidExp={
                        sdProductDetailsPageComplementMktValidExp
                      }
                      sdProductDetailsComplementMktClick={sdProductDetailsComplementMktClick}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* 导航按钮 */}
              {recommendList.length > 4 && (
                <SwiperNavigationButtons
                  navigationPrefix="related-recommend"
                  prevButtonPosition="left-[40px] !top-[10vw]"
                  nextButtonPosition="right-[40px] !top-[10vw]"
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // 移动端布局
  return (
    <div className="similar-goods">
      <div className="h-10 bg-auxiliary-gray" />
      <div className="similar-goods mx-auto pt-20 pb-[30px] bg-white">
        <div className="title px-14 mb-16 font-bb">{t('title')}</div>

        {shouldShowSkeleton ? (
          // 移动端骨架屏 - 横向滚动布局
          <div className="flex gap-[8px] px-[14px] overflow-hidden">
            {skeletonList.map((_, index) => (
              <div key={index} className="flex-shrink-0 w-[160px]">
                <SkeletonItemMobile />
              </div>
            ))}
          </div>
        ) : (
          <Swiper
            className="might-like-swiper"
            slidesPerView="auto"
            spaceBetween={8}
            slidesOffsetBefore={14}
            slidesOffsetAfter={14}
            observer
            observeParents
            observeSlideChildren
          >
            {recommendList.map((item, index) => (
              <SwiperSlide key={index} className="!w-[160px] !h-auto">
                <div className="list_item">
                  <ItemComponent
                    product={item}
                    uuid={uuid}
                    index={index}
                    sdProductDetailsPageComplementMktExp={sdProductDetailsPageComplementMktExp}
                    sdProductDetailsPageComplementMktValidExp={
                      sdProductDetailsPageComplementMktValidExp
                    }
                    sdProductDetailsComplementMktClick={sdProductDetailsComplementMktClick}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </div>
  );
};

export default RelatedRecommendList;
